import { externalMapLink } from "../../../geo/service";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Input,
  Card,
  Row,
  Col,
  Tooltip,
  Modal,
  Typography,
  Space,
  Pagination,
  message,
  Spin,
  Empty,
} from "antd";
import {
  EnvironmentOutlined,
  HomeOutlined,
  BankOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import InstitutionsForm from "./InstitutionsForm";
import { useUser } from "../../../UserContext";
import "./Institutions.css";

const { Search } = Input;
const { Text } = Typography;

// Componente memoizado para las cartas de institución
const InstitutionCard = React.memo(({ 
  institution, 
  hasPermission, 
  openInGoogleMaps, 
  handleClickOpen, 
  handleDeleteDialogOpen 
}) => (
  <Card
    title={institution.nombre}
    variant="borderless"
    actions={[
      <Tooltip title="Ver en Google Maps" key="google-maps">
        <EnvironmentOutlined
          onClick={() => openInGoogleMaps(institution.coordenadas.coordinates)}
        />
      </Tooltip>,
      hasPermission("EDITAR_I") && (
        <Tooltip title="Editar" key="edit">
          <EditOutlined onClick={() => handleClickOpen(institution)} />
        </Tooltip>
      ),
      hasPermission("ELIMINAR_I") && (
        <Tooltip title="Borrar" key="delete">
          <DeleteOutlined onClick={() => handleDeleteDialogOpen(institution)} />
        </Tooltip>
      ),
    ].filter(Boolean)}
  >
    <Row gutter={[8, 8]}>
      <Col span={12}>
        <BankOutlined />{" "}
        <Text>{institution.departamento?.nombre || "Sin departamento"}</Text>
      </Col>
      <Col span={12}>
        <HomeOutlined /> <Text>{institution.direccion}</Text>
      </Col>
      <Col span={12}>
        <EnvironmentOutlined />{" "}
        <Text>{institution.ciudad?.nombre || "Sin ciudad"}</Text>
      </Col>
      <Col span={12}>
        <EnvironmentOutlined />{" "}
        <Text>{institution.barrio}</Text>
      </Col>
      {institution.indicaciones && (
        <Col span={24}>
          <InfoCircleOutlined />{" "}
          <Text>{institution.indicaciones}</Text>
        </Col>
      )}
    </Row>
  </Card>
));

const Institutions = () => {
  const { user: currentUser } = useUser();
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentInstitution, setCurrentInstitution] = useState(null);
  const [institutionToDelete, setInstitutionToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const isInitialMount = useRef(true);
  const currentFetchRef = useRef(null);

  // Función de permisos
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find(
        (m) => m.key === "Instituciones"
      );
      if (!module || module.estado !== "disponible") return false;
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Instituciones" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Debounce en la búsqueda
  useEffect(() => {
    const handler = setTimeout(() => {
      // Verificar si realmente cambió la query para evitar actualizaciones innecesarias
      if (searchQuery !== debouncedSearchQuery) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 800);
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery, debouncedSearchQuery]);

  const fetchInstitutions = useCallback(
    async (query = "", page = 1, limit = 10) => {
      // Cancelar request anterior si existe
      if (currentFetchRef.current) {
        currentFetchRef.current.abort();
      }

      // Crear nuevo controller para cancelación
      const controller = new AbortController();
      currentFetchRef.current = controller;


      setLoading(true);
      try {
        const response = await api.get("/instituciones/list", {
          params: { q: query, page, limit },
          signal: controller.signal,
        });
        
        // Solo actualizar si esta request no fue cancelada
        if (!controller.signal.aborted) {
          const { data, pagination } = response.data;
          setFilteredInstitutions(Array.isArray(data) ? data : []);
          setCurrentPage(pagination?.page || 1);
          setTotalItems(pagination?.total || 0);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          console.error("Error al obtener las instituciones:", error);
          message.error("Ocurrió un error al cargar las instituciones.");
          setFilteredInstitutions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
        currentFetchRef.current = null;
      }
    },
    [] // No dependencies needed since we're not using external variables
  );

  // Cargar datos solo cuando sea necesario
  useEffect(() => {
    // En el primer montaje, cargar con query vacía
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchInstitutions("", 1, pageSize);
    } else {
      fetchInstitutions(debouncedSearchQuery, currentPage, pageSize);
    }
  }, [debouncedSearchQuery, currentPage, pageSize, fetchInstitutions]);

  // Cleanup para cancelar requests pendientes al desmontar
  useEffect(() => {
    return () => {
      if (currentFetchRef.current) {
        currentFetchRef.current.abort();
      }
    };
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  const handleClickOpen = useCallback((institution = null) => {
    setCurrentInstitution(institution);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setCurrentInstitution(null);
  }, []);

  const handleDeleteDialogOpen = useCallback((institution) => {
    setInstitutionToDelete(institution);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setInstitutionToDelete(null);
  }, []);

  const handleDelete = async () => {
    if (institutionToDelete) {
      try {
        await api.delete(`/instituciones/${institutionToDelete.id}`, {
          data: { usuario_id: currentUser.id },
        });
        message.success("Institución eliminada correctamente.");
        fetchInstitutions(debouncedSearchQuery, currentPage, pageSize);
        handleDeleteDialogClose();
      } catch (error) {
        console.error("Error al eliminar la institución:", error);
        message.error("No se pudo eliminar la institución.");
      }
    }
  };

  const openInGoogleMaps = useCallback((coordinates) => {
    const [lat, lon] = coordinates;
    window.open(
      externalMapLink(lon,lat),
      "_blank"
    );
  }, []);

  return (
    <div style={{ padding: 16 }}>
      {/* Barra de Búsqueda y Botón para Agregar */}
      <Space
        style={{
          marginBottom: 20,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Search
          placeholder="Buscar institución"
          value={searchQuery}
          onChange={handleSearchChange}
          enterButton={<SearchOutlined />}
          style={{ width: "100%", maxWidth: 300 }}
        />
        {hasPermission("CREAR_I") && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleClickOpen()}
            style={{ marginRight: 10 }}
          >
            Agregar Nueva Institución
          </Button>
        )}
      </Space>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 50 }}>
          <Spin size="large" tip="Cargando instituciones...">
            <div style={{ height: "200px" }} />
          </Spin>
        </div>
      ) : (
        <>
          {filteredInstitutions.length === 0 ? (
            <Empty description="No se encontraron instituciones." />
          ) : (
            <Row gutter={[16, 16]}>
              {filteredInstitutions.map((institution) => (
                <Col xs={24} sm={12} md={8} key={institution.id}>
                  <InstitutionCard
                    institution={institution}
                    hasPermission={hasPermission}
                    openInGoogleMaps={openInGoogleMaps}
                    handleClickOpen={handleClickOpen}
                    handleDeleteDialogOpen={handleDeleteDialogOpen}
                  />
                </Col>
              ))}
            </Row>
          )}

          {/* Paginación */}
          {filteredInstitutions.length > 0 && (
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalItems}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              style={{ marginTop: 20, textAlign: "center" }}
            />
          )}
        </>
      )}

      {open && hasPermission(currentInstitution ? "EDITAR_I" : "CREAR_I") && (
        <InstitutionsForm
          open={open}
          handleClose={handleClose}
          fetchInstitutions={() =>
            fetchInstitutions(debouncedSearchQuery, currentPage, pageSize)
          }
          currentInstitution={currentInstitution}
        />
      )}

      {deleteDialogOpen && hasPermission("ELIMINAR_I") && (
        <Modal
          title="Confirmar Eliminación"
          open={deleteDialogOpen}
          onOk={handleDelete}
          onCancel={handleDeleteDialogClose}
          okText="Borrar"
          cancelText="Cancelar"
        >
          <p>¿Estás seguro de que deseas eliminar esta institución?</p>
        </Modal>
      )}
    </div>
  );
};
export default Institutions;
